import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login, register } from '@/lib/api';
import { useNexusStore } from '@/stores/nexusStore';
import { Hexagon, LogIn, UserPlus, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setAuth = useNexusStore((s) => s.setAuth);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        result = await register(email, password, name);
      }
      setAuth(result.token, result.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex items-center justify-center bg-[var(--nexus-bg-base)]" style={{ backgroundImage: 'radial-gradient(600px circle at 50% 30%, rgba(6,182,212,0.06), transparent 55%)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--nexus-accent)] bg-opacity-15 mb-4">
            <Hexagon size={28} className="text-[var(--nexus-accent)]" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[var(--nexus-text-primary)] tracking-tight">NEXUS ITSM</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-1">AI-Native IT Service Management</p>
        </div>

        {/* Card */}
        <div className="nexus-card relative overflow-hidden p-6">
          <h2 className="text-lg font-semibold text-[var(--nexus-text-primary)] mb-4">
            {isLogin ? 'Sign In' : 'Create Account'}
          </h2>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-[var(--nexus-danger)] bg-opacity-10 border border-red-500/20" data-testid="login-error">
              <AlertCircle size={14} className="text-[var(--nexus-danger)]" />
              <span className="text-xs text-red-300">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Name</label>
                <input data-testid="register-name" type="text" value={name} onChange={e => setName(e.target.value)} required={!isLogin}
                  className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)] transition-colors"
                  placeholder="Full name" />
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)] transition-colors"
                placeholder="admin@nexus.corp" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required
                className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)] transition-colors"
                placeholder="Enter password" />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--nexus-primary-bright)] text-white text-sm font-medium hover:bg-[var(--nexus-primary-bright)]/90 disabled:opacity-50 transition-colors">
              {isLogin ? <LogIn size={14} /> : <UserPlus size={14} />}
              {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <div className="mt-4 text-center space-y-2">
            <button onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-xs text-[var(--nexus-accent)] hover:underline" data-testid="toggle-auth-mode">
              {isLogin ? 'Need an account? Register' : 'Already have an account? Sign In'}
            </button>
            {isLogin && <div><Link to="/password-reset" className="text-xs text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)]">Forgot password?</Link></div>}
          </div>

          {/* Demo credentials */}
          <div className="mt-4 p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
            <p className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-1">Demo Credentials</p>
            <p className="text-[11px] text-[var(--nexus-text-secondary)] font-mono">admin@nexus.corp / admin123</p>
            <p className="text-[11px] text-[var(--nexus-text-secondary)] font-mono">ops@nexus.corp / ops123</p>
          </div>
        </div>
      </div>
    </div>
  );
}
